import { useCallback, useEffect, useState } from 'react';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { sdk } from 'src/utilities/sdk';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { IGroup, LONG_SEARCH_DELAY } from 'src/utilities/useSearchHooks';

interface IRemoveGroupModalStatus {
  showModal: boolean;
  groupId: string;
  groupName: string;
}

type UseRemoveGroupProps = {
  corpId: string;
  onSuccess: () => void;
};

type UseRemoveGroupReturn = {
  groupIdDeletionInProgress: string;
  removeGroupModalStatus: IRemoveGroupModalStatus;
  removeGroup: (groupId: string) => Promise<void>;
  onRemoveGroup: (group: IGroup) => void;
  closeRemoveGroupModal: () => void;
};

export const useRemoveGroup = ({
  corpId,
  onSuccess,
}: UseRemoveGroupProps): UseRemoveGroupReturn => {
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();

  const [removeGroupModalStatus, setRemoveGroupModalStatus] =
    useState<IRemoveGroupModalStatus>({
      showModal: false,
      groupId: null,
      groupName: null,
    });

  const [groupIdDeletionInProgress, setGroupIdDeletionInProgress] =
    useState<string>(null);

  async function removeGroup(groupId: string): Promise<void> {
    try {
      setGroupIdDeletionInProgress(groupId);

      await startAsyncTask(() => {
        return sdk.http.delete(`/corp-admin/${corpId}/groups/v2/${groupId}`);
      });
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: 'There was a problem removing this group. Try again.',
      });
    }
  }

  const handleAsyncStatus = useCallback(async () => {
    switch (asyncTaskStatus) {
      case AsyncTaskStatus.COMPLETE:
        /**
         * The reason for this timeout is that elastic search needs some time to rearrange the indexes
         * after a record is deleted. Without this timeout the search could potentially
         * return outdated results
         */
        setTimeout(() => {
          setGroupIdDeletionInProgress(null);
          closeRemoveGroupModal();
          pushSuccess('This group has been removed.');

          onSuccess();
        }, LONG_SEARCH_DELAY);
        break;

      case AsyncTaskStatus.ERROR:
        setGroupIdDeletionInProgress(null);
        closeRemoveGroupModal();
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncTaskStatus]);

  function onRemoveGroup(group: IGroup) {
    setRemoveGroupModalStatus({
      groupId: group.id,
      groupName: group.name,
      showModal: true,
    });
  }

  function closeRemoveGroupModal() {
    setRemoveGroupModalStatus({
      showModal: false,
      groupId: null,
      groupName: null,
    });
  }

  useEffect(() => {
    handleAsyncStatus();
  }, [handleAsyncStatus]);

  return {
    removeGroupModalStatus,
    groupIdDeletionInProgress,
    removeGroup,
    onRemoveGroup,
    closeRemoveGroupModal,
  };
};
